import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';

import { AddContactModal } from '@/components/provider-directory';
import { DirectoryContactCard } from '@/components/treatment/medical-provider-details/DirectoryContactCard';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DirectoryHandle,
  DirectoryProviderUnit,
  useProviderDirectory,
} from '@/hooks/useProviderDirectory';
import { sortHandlesByPreference } from '@/utils/providerDirectory';

const ALLOWED_HANDLE_TYPES = new Set(['FAX', 'EMAIL', 'MAILING_ADDRESS']);

interface DirectoryContactInlinePickerProps {
  providerRefId: string;
  selectedHandleId: string | null;
  onSelectHandle: (
    handle: DirectoryHandle,
    unit: DirectoryProviderUnit,
  ) => void;
  maxVisible?: number;
  showAddButton?: boolean;
  compact?: boolean;
}

export const DirectoryContactInlinePicker = ({
  providerRefId,
  selectedHandleId,
  onSelectHandle,
  maxVisible = 3,
  showAddButton = true,
  compact = false,
}: DirectoryContactInlinePickerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);

  const { provider, units, isLoading, refetch } = useProviderDirectory({
    providerRefId,
    skip: !providerRefId,
  });

  const handlesByUnit = useMemo(() => {
    return units
      .map((unit) => ({
        unit,
        handles: (unit.handles || []).filter((handle) =>
          ALLOWED_HANDLE_TYPES.has(
            (handle.handle?.handle_type || '').toUpperCase(),
          ),
        ),
      }))
      .map(({ unit, handles }) => ({
        unit,
        handles: sortHandlesByPreference(handles),
      }))
      .filter(({ handles }) => handles.length > 0);
  }, [units]);

  const allHandles = useMemo(() => {
    return handlesByUnit.flatMap(({ unit, handles }) =>
      handles.map((handle) => ({ handle, unit })),
    );
  }, [handlesByUnit]);

  const visibleHandles = isExpanded
    ? allHandles
    : allHandles.slice(0, maxVisible);
  const hasMore = allHandles.length > maxVisible;

  if (!providerRefId) {
    return (
      <Card className="p-3 border border-amber-200 bg-amber-50">
        <p className="text-xs text-amber-700">
          Link this provider to the directory to select vetted contacts.
        </p>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="p-3">
        <div className="text-xs text-gray-500">Loading contacts…</div>
      </Card>
    );
  }

  if (!provider) {
    return (
      <Card className="p-3 border border-red-200 bg-red-50">
        <p className="text-xs text-red-700">
          Unable to load directory contacts.
        </p>
      </Card>
    );
  }

  const hasEligibleHandles = allHandles.length > 0;

  return (
    <div className="space-y-2">
      {!hasEligibleHandles ? (
        <Card className="p-3 border border-dashed border-blue-300 bg-blue-50">
          <p className="text-xs text-blue-700">
            No fax, email, or mailing contacts yet.{' '}
            {showAddButton && 'Add one to get started.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {visibleHandles.map(({ handle, unit }) => (
            <Fragment key={handle.id}>
              <DirectoryContactCard
                handle={handle}
                unit={unit}
                isSelected={selectedHandleId === String(handle.id)}
                compact={compact}
                onSelect={() => onSelectHandle(handle, unit)}
              />
            </Fragment>
          ))}

          {hasMore && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full text-xs"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-3 w-3 mr-1" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3 mr-1" />
                  Show {allHandles.length - maxVisible} More
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {showAddButton && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsAddContactOpen(true)}
          className="w-full text-xs"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Directory Contact
        </Button>
      )}

      <AddContactModal
        providerId={providerRefId}
        units={units}
        open={isAddContactOpen}
        onClose={() => setIsAddContactOpen(false)}
        onSuccess={() => {
          refetch();
          setIsAddContactOpen(false);
        }}
      />
    </div>
  );
};
